import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useFindMemeberIdApi, usePostSendEmailVerificationApi, usePostVerifyCodeApi } from '@/api/auth';
import { PrimaryButton } from '@/components/common/Button/style';
import Field from '@/components/common/Field/Field';
import { EmailRow } from '@/components/common/form/formPageStyles';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { emailOptions } from '@/constants/option';
import { useModalStore } from '@/store/modalStore';

import * as S from './style';

const FindId = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { showModal } = useModalStore();
	const [name, setName] = useState('');
	const [email, setEmail] = useState({ front: '', back: '', code: '직접입력', verificationCode: '' });
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isSendEmail, setIsSendEmail] = useState(false);
	const { mutateAsync: VerifyCode } = usePostVerifyCodeApi();
	const { mutateAsync: SendEmailVerification, isPending } = usePostSendEmailVerificationApi();
	const { mutateAsync: findMemberId } = useFindMemeberIdApi();

	const resetEmailVerification = () => {
		setIsSendEmail(false);
		setIsEmailVerified(false);
		setEmail((prev) => ({ ...prev, verificationCode: '' }));
	};

	const handleSendEmailVerification = async () => {
		const res = await SendEmailVerification({ email: `${email.front}@${email.back}` });
		if (res.data.success) {
			showModal(t('common.alerts.emailVerificationSent'));
			setIsSendEmail(true);
			setIsEmailVerified(false);
			setEmail((prev) => ({ ...prev, verificationCode: '' }));
		} else {
			showModal(t('common.alerts.emailVerificationFailed'));
			resetEmailVerification();
		}
	};

	const handleVerifyCode = async () => {
		const emailResult = `${email.front}@${email.back}`;
		const res = await VerifyCode({ email: emailResult, code: email.verificationCode });
		if (res.data.success) {
			showModal(t('common.alerts.verificationCompleted'));
			setIsEmailVerified(true);
		} else {
			showModal(t('common.alerts.verificationCodeMismatch'));
			setIsEmailVerified(false);
		}
	};

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const res = await findMemberId({ member_name: name, member_email: `${email.front}@${email.back}` });
		if (res.data.success) {
			if (res.data.data.member_id !== null) {
				navigate('/auth/findid/complete', {
					state: {
						member_id: res.data.data.member_id,
					},
				});
			} else {
				showModal(t('해당하는 회원이 없습니다.'));
			}
		}
	};

	return (
		<S.Page>
			<S.Container>
				<S.PageHeader>
					<S.Eyebrow>Account</S.Eyebrow>
					<S.Title>{t('아이디 찾기')}</S.Title>
					<S.Subtitle>{t('auth.findId.subtitle')}</S.Subtitle>
				</S.PageHeader>

				<S.FormCard>
					<S.InnerForm onSubmit={onSubmit} noValidate>
					<S.FieldBox>
						<Field title="이름" error="">
							<InputText
								placeholder="signup.basicInfo.placeholders.name"
								value={name}
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setName(e.target.value);
								}}
							/>
						</Field>
						<Field title="common.emailVerification" gap="0.8rem">
							<Field>
								<EmailRow>
									<InputText
										input={(e: React.ChangeEvent<HTMLInputElement>) => {
											setEmail((prev) => ({ ...prev, front: e.target.value }));
											resetEmailVerification();
										}}
										value={email.front}
									/>
									<span>@</span>
									<InputText
										input={(e: React.ChangeEvent<HTMLInputElement>) => {
											setEmail((prev) => ({ ...prev, back: e.target.value }));
											resetEmailVerification();
										}}
										value={email.code !== '' && email.code !== '직접입력' ? email.code : email.back}
										readonly={email.code !== '' && email.code !== '직접입력'}
									/>
									<InputSelect
										options={emailOptions}
										selectedOption={email.code}
										setSelectedOption={(val) => {
											setEmail((prev) => ({ ...prev, code: val }));
											resetEmailVerification();
											if (val !== '직접입력') {
												setEmail((prev) => ({ ...prev, back: val }));
											}
										}}
										defaultValue="common.directInput"
									/>
									<S.FindButton
										type="button"
										onClick={handleSendEmailVerification}
										disabled={email.front === '' || email.back === '' || email.code === '' || isPending}
									>
										{!isSendEmail ? t('common.actions.send') : t('common.actions.resend')}
									</S.FindButton>
								</EmailRow>
								{isSendEmail && (
									<>
										<S.VerifyRow>
											<InputText
												value={email.verificationCode}
												input={(e: React.ChangeEvent<HTMLInputElement>) => {
													setEmail((prev) => ({ ...prev, verificationCode: e.target.value }));
													setIsEmailVerified(false);
												}}
												placeholder="common.placeholders.verificationCode"
											/>
											<S.FindButton type="button" onClick={handleVerifyCode} disabled={email.verificationCode === '' || isEmailVerified}>
												{t('common.actions.verify')}
											</S.FindButton>
										</S.VerifyRow>
										<S.Danger>{t('common.emailVerificationGuide')}</S.Danger>
									</>
								)}
							</Field>
						</Field>
						<S.FormActions>
							<PrimaryButton
								type="submit"
								disabled={
									name === '' ||
									email.front === '' ||
									email.back === '' ||
									email.code === '' ||
									!isEmailVerified ||
									(isSendEmail && email.verificationCode === '')
								}
							>
								{t('아이디 찾기')}
							</PrimaryButton>
						</S.FormActions>
					</S.FieldBox>
					</S.InnerForm>
				</S.FormCard>
			</S.Container>
		</S.Page>
	);
};

export default FindId;
